"use client";

import { Children, cloneElement, forwardRef, isValidElement, useCallback, useEffect, useId, useRef, useState, type HTMLAttributes, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";
import { useAnchoredPosition, type Side } from "./floating";
import { useIsClient } from "./hooks";
import { useEscapeStack } from "./overlayStack";

// Stage 5 ported this from @datasirpi/ui. Phase 4.4 rewrites its positioning
// and its hide behaviour; the trigger-cloning shape and the show/hide timers
// are unchanged, so existing call sites need no edits.
//
// Two real defects are fixed here, both from finding F15:
//
// 1. CLIPPING. The panel was an absolutely-positioned child of the trigger,
//    so any ancestor with `overflow: auto` cut it off — which in this app
//    means every table (`.rc-datatable-wrap`) and every scrolling toolbar.
//    Tooltips on table-row actions, the single largest group of icon-only
//    buttons in the product, were unreadable. It now renders through a
//    portal at fixed coordinates, so nothing between trigger and panel can
//    clip it.
//
// 2. NOT HOVERABLE (WCAG 1.4.13 "Content on Hover or Focus"). The tooltip
//    closed the instant the pointer left the trigger, so a user could not
//    move onto the tooltip itself — which the success criterion requires,
//    because tooltip content must be reachable for people who magnify the
//    screen or who need to read it slowly. The panel now keeps itself open
//    while hovered, and the hide delay gives the pointer time to cross the
//    gap between trigger and panel.
//
// The dismissal path satisfies 1.4.13's "dismissible" clause: Escape closes
// the tooltip without moving focus. d2d-XDR eval F17: it now goes through the
// overlay stack. Handled locally, it never ran inside a Modal or Drawer (the
// stack's old capture-phase listener stopped the event first); on the stack,
// an open tooltip is the top layer, so the first Escape dismisses the tooltip
// and leaves the dialog open, which is exactly what 1.4.13 asks for.
//
// Keyboard focus opens it only on :focus-visible. Modal and Drawer focus
// their close button on open; after a mouse click that focus is not
// focus-visible, so a "Close dialog" tooltip no longer pops on every open
// (and no longer costs an extra Escape).
export type TooltipSide = Side;

export interface TooltipProps extends Omit<HTMLAttributes<HTMLDivElement>, "content"> {
  content: ReactNode;
  side?: TooltipSide;
  showDelay?: number;
  hideDelay?: number;
  children: ReactElement;
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
  { content, side = "top", showDelay = 200, hideDelay = 120, children, className, id: providedId, ...rest },
  ref
) {
  const fallbackId = useId();
  const tooltipId = providedId ?? fallbackId;
  const [open, setOpen] = useState(false);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Portals need a DOM target, which does not exist during the server render.
  const mounted = useIsClient();

  const child = Children.only(children);
  if (!isValidElement(child)) {
    throw new Error("Tooltip requires a single React element child.");
  }

  const scheduleShow = useCallback(() => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    if (open || showTimer.current) return;
    showTimer.current = setTimeout(() => {
      setOpen(true);
      showTimer.current = null;
    }, showDelay);
  }, [open, showDelay]);

  const scheduleHide = useCallback(() => {
    if (showTimer.current) {
      clearTimeout(showTimer.current);
      showTimer.current = null;
    }
    if (hideTimer.current) return;
    hideTimer.current = setTimeout(() => {
      setOpen(false);
      hideTimer.current = null;
    }, hideDelay);
  }, [hideDelay]);

  useEscapeStack(open, () => setOpen(false));

  useEffect(() => () => {
    if (showTimer.current) clearTimeout(showTimer.current);
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, []);

  /* The wrapper span is `display: contents` (globals.css), so it has no box of
     its own — position against the trigger element inside it. */
  const triggerRef = useRef<{ readonly current: HTMLElement | null }>({
    get current() {
      const wrap = anchorRef.current;
      return (wrap?.firstElementChild as HTMLElement | null) ?? wrap;
    },
  }).current;
  const pos = useAnchoredPosition({ open, anchorRef: triggerRef, panelRef, side, align: "center", offset: 6 });

  const hasContent = content !== undefined && content !== null && content !== "";
  if (!hasContent) return child;

  const {
    onMouseEnter: triggerOnMouseEnter,
    onMouseLeave: triggerOnMouseLeave,
    onFocus: triggerOnFocus,
    onBlur: triggerOnBlur,
    "aria-describedby": triggerDescribedBy,
  } = child.props as {
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
    "aria-describedby"?: string;
  };

  // React 19 makes `ref` an ordinary prop, so `child` (an arbitrary
  // consumer-supplied trigger element) could carry one — this repo's
  // react-hooks/refs rule flags any generic function call receiving a value
  // that might hold a ref. cloneElement is exactly the React API built to do
  // that safely (it preserves the original ref), and there is no JSX-syntax
  // equivalent for "attach more props to an existing element instance".
  // eslint-disable-next-line react-hooks/refs
  const enhanced = cloneElement(child, {
    "aria-describedby": [triggerDescribedBy, open ? tooltipId : null].filter(Boolean).join(" ") || undefined,
    onMouseEnter: (e: React.MouseEvent) => { triggerOnMouseEnter?.(e); scheduleShow(); },
    onMouseLeave: (e: React.MouseEvent) => { triggerOnMouseLeave?.(e); scheduleHide(); },
    onFocus: (e: React.FocusEvent) => {
      triggerOnFocus?.(e);
      let visible = true;
      try { visible = (e.target as HTMLElement).matches(":focus-visible"); } catch { /* old engines: show */ }
      if (visible) scheduleShow();
    },
    onBlur: (e: React.FocusEvent) => { triggerOnBlur?.(e); scheduleHide(); },
  } as Record<string, unknown>);

  const panel = open && mounted ? createPortal(
    <div
      ref={(node) => {
        panelRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      id={tooltipId}
      role="tooltip"
      data-side={pos.side}
      data-open={pos.ready}
      className={cn("rc-tooltip", className)}
      style={{ left: pos.x, top: pos.y }}
      // WCAG 1.4.13 "hoverable": keeping the pointer on the tooltip keeps it
      // open, so its content can actually be read and selected.
      onMouseEnter={scheduleShow}
      onMouseLeave={scheduleHide}
      {...rest}
    >
      {content}
    </div>,
    document.body
  ) : null;

  return (
    <span ref={anchorRef} className="rc-tooltip-wrap">
      {enhanced}
      {panel}
    </span>
  );
});
